import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useAccount, useChainId } from 'wagmi';

import Button from "../../components/Button";
import { selectId, selectUwuUrl } from "../../state/uwuSlice";
import Options from "./Options";
import Uwu from "./Uwu";
import UwuSelector from "./UwuSelector";
import downloadIcon from "../../assets/ui/download.svg";
import copyIcon from "../../assets/ui/copy.svg";
import checkIcon from "../../assets/ui/check.svg";
import shareIcon from "../../assets/ui/share.svg";

const StyledHomePage = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  flex: 1;
  background: var(--bg);

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ButtonContainer = styled.div`
  height: auto;
  margin-top: 1rem;
  padding-bottom: 3rem; /* Maximum padding to ensure button shadow is fully visible */
  display: flex;

  button {
    margin: 0 1rem;
  }

  @media (max-width: 600px) {
    margin-bottom: 3rem;
  }
`;

const ChangeButton = styled.button`
  padding: 1rem 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.8px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  cursor: pointer;

  border: 2px solid var(--primary);
  background: var(--bg);
  color: var(--main);

  &:hover {
    background: var(--primary-light);
    border-color: var(--primary);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const IconButton = styled.button`
  background: var(--bg);
  border: 2px solid var(--primary);
  border-radius: 8px;
  color: var(--main);
  cursor: pointer;
  font-size: 1.8rem;
  padding: 1rem;
  margin: 0 1rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 48px;
  aspect-ratio: 1;
  
  &:hover {
    background: var(--primary-light);
    border-color: var(--primary);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const CopyButton = styled(IconButton)`
  background: ${props => props.style?.background || 'var(--bg)'};
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
  filter: brightness(0) saturate(100%) invert(24%) sepia(10%) saturate(1044%) hue-rotate(314deg) brightness(95%) contrast(92%);
  
  ${IconButton}:hover & {
    filter: brightness(0) saturate(100%) invert(64%) sepia(28%) saturate(1735%) hue-rotate(310deg) brightness(91%) contrast(89%);
  }
`;

const CaptureArea = styled.div`
  @media (max-width: 600px) {
    margin-top: 1rem;
  }
`;

const HomePage = (): JSX.Element => {
  const id = useSelector(selectId);
  const url = useSelector(selectUwuUrl);
  const { address } = useAccount();
  const chainId = useChainId();

  const [showUwuSelector, setShowUwuSelector] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const getDownloadLink = async (): Promise<void> => {
    if (!url) return;
    try {
      const image = await fetch(url);
      const imageBlog = await image.blob();
      const imageURL = URL.createObjectURL(imageBlog);
      setLink(imageURL);
    } catch (error) {
      console.log('CORS blocked, using direct URL for download');
      // Fallback: use the direct URL for download (works for download even with CORS)
      setLink(url);
    }
  };

  const copyImageToClipboard = async (): Promise<void> => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create a ClipboardItem with the image blob
      const clipboardItem = new ClipboardItem({
        [blob.type]: blob
      });
      
      await navigator.clipboard.write([clipboardItem]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Hide success message after 2 seconds
      console.log('Image copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy image to clipboard (likely CORS):', error);
      // Fallback: copy the URL instead
      try {
        await navigator.clipboard.writeText(url);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        console.log('Image URL copied to clipboard!');
      } catch (urlError) {
        console.error('Failed to copy URL:', urlError);
        alert('Unable to copy image. You can right-click the image and select "Copy image" manually.');
      }
    }
  };

  const shareToTwitter = (): void => {
    if (!id) return;
    
    const text = `Just decorated my uwu #${id} in the @uwucrewnft Dressing Room! Check out my creation! ✨`;
    const url = window.location.href;
    
    // Create Twitter share URL
    const twitterUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}`;
    
    // Open in new tab
    window.open(twitterUrl, '_blank');
  };

  useEffect(() => {
    setShowUwuSelector(true);
  }, [address, chainId]);

  useEffect(() => {
    getDownloadLink();
  }, [url]);

  return (
    <StyledHomePage>
      <Container>
        <CaptureArea ref={captureRef}>
          <Uwu />
        </CaptureArea>
        <ButtonContainer>
          <ChangeButton onClick={() => setShowUwuSelector(true)}>change uwu</ChangeButton>
          {id && link && url && (
            <>
              <a
                download={`uwu-${id}.${
                  url.indexOf("transparent") > -1 ? "png" : "jpg"
                }`}
                href={link}
                title={`Download uwu-${id}.${
                  url.indexOf("transparent") > -1 ? "png" : "jpg"
                }`}
              >
                <IconButton onClick={() => console.log("meow")}>
                  <Icon src={downloadIcon} alt="Download" />
                </IconButton>
              </a>
              <IconButton 
                onClick={copyImageToClipboard}
                title="Copy image to clipboard"
                style={{
                  transform: copySuccess ? 'scale(1.05)' : undefined
                }}
              >
                {copySuccess ? (
                  <Icon src={checkIcon} alt="Copied" />
                ) : (
                  <Icon src={copyIcon} alt="Copy" />
                )}
              </IconButton>
              <IconButton 
                onClick={shareToTwitter}
                title="Share on X/Twitter"
              >
                <Icon src={shareIcon} alt="Share" />
              </IconButton>
            </>
          )}
        </ButtonContainer>
      </Container>
      <Container>
        <Options address={address} />
      </Container>
      <UwuSelector
        address={address}
        show={showUwuSelector}
        close={() => {
          setShowUwuSelector(false);
          getDownloadLink();
        }}
      />
    </StyledHomePage>
  );
};

export default HomePage;
